import {
  Box,
  Button,
  Chip,
  Drawer,
  Icon,
  IconButton,
  Paper,
  Tab,
  Tabs,
  Typography,
} from '@shared/ui';
import React from 'react';
import type { ExecutionStep } from './useAgentStreaming';

interface InspectorPanelProps {
  step: ExecutionStep | null;
  open: boolean;
  onClose: () => void;
  onRetry?: (step: ExecutionStep) => void;
  onEditPrompt?: (step: ExecutionStep) => void;
}

function TabPanel({
  children,
  value,
  index,
}: {
  children: React.ReactNode;
  value: number;
  index: number;
}) {
  return (
    <div role="tabpanel" hidden={value !== index}>
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

export default function InspectorPanel({
  step,
  open,
  onClose,
  onRetry,
  onEditPrompt,
}: InspectorPanelProps) {
  const [activeTab, setActiveTab] = React.useState(0);

  if (!step) return null;

  const stepTitle = (() => {
    if (step.type === 'planner_decision') {
      const decision = step.data?.decision;
      if (decision === 'start') return 'Planning Start';
      if (decision === 'complete') return 'Planning Complete';
      return `Iteration ${step.iteration ?? '?'}`;
    }
    if (step.type === 'tool_call') return `Tool: ${step.data?.name ?? 'Unknown'}`;
    if (step.type === 'tool_result') return 'Tool Result';
    if (step.type === 'executor_content') return 'Response Output';
    return 'Step Details';
  })();

  const statusColor =
    step.status === 'complete' ? 'success' : step.status === 'error' ? 'error' : 'primary';

  const copyToClipboard = (text: string) => {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(text);
    }
  };

  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      <Box sx={{ width: 450, height: '100%', display: 'flex', flexDirection: 'column' }}>
        {/* Header */}
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
          <div className="flex items-start justify-between mb-2">
            <div className="flex-1">
              <Typography variant="h6" className="mb-1">
                {stepTitle}
              </Typography>
              <Chip size="small" label={step.status} color={statusColor as any} />
              {typeof step.duration === 'number' && (
                <Chip size="small" label={`${Math.round(step.duration)}ms`} className="ml-2" />
              )}
            </div>
            <IconButton onClick={onClose} size="small">
              <Icon>close</Icon>
            </IconButton>
          </div>

          {/* Action buttons for failed steps */}
          {step.status === 'error' && (
            <div className="flex gap-2 mt-2">
              {onRetry && (
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<Icon>refresh</Icon>}
                  onClick={() => onRetry(step)}
                >
                  Retry
                </Button>
              )}
              {onEditPrompt && (
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<Icon>edit</Icon>}
                  onClick={() => onEditPrompt(step)}
                >
                  Edit Prompt
                </Button>
              )}
            </div>
          )}
        </Box>

        {/* Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} variant="fullWidth">
            <Tab label="Overview" icon={<Icon>info</Icon>} iconPosition="start" />
            <Tab label="Data" icon={<Icon>data_object</Icon>} iconPosition="start" />
            {step.data?.reasoning && (
              <Tab label="Thinking" icon={<Icon>psychology</Icon>} iconPosition="start" />
            )}
          </Tabs>
        </Box>

        {/* Content */}
        <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
          <TabPanel value={activeTab} index={0}>
            {/* Overview Tab */}
            <div className="space-y-4">
              {/* Reasoning */}
              {step.data?.reasoning && (
                <Paper variant="outlined" className="p-3 bg-blue-50">
                  <Typography variant="caption" className="font-semibold block mb-2">
                    💭 AI Thinking
                  </Typography>
                  <Typography variant="body2">{step.data.reasoning}</Typography>
                </Paper>
              )}

              {/* Reflection */}
              {step.data?.reflection_text && (
                <Paper variant="outlined" className="p-3 bg-purple-50">
                  <Typography variant="caption" className="font-semibold block mb-2">
                    🔍 Reflection
                  </Typography>
                  <Typography variant="body2" className="whitespace-pre-wrap">
                    {step.data.reflection_text}
                  </Typography>
                </Paper>
              )}

              {/* Tool Information */}
              {step.type === 'tool_call' && step.data && (
                <div>
                  <Typography variant="caption" className="font-semibold block mb-2">
                    🔧 Tool Call
                  </Typography>
                  <Paper variant="outlined" className="p-3">
                    <Typography variant="body2">
                      <strong>Name:</strong> {step.data.name || 'Unknown'}
                    </Typography>
                    {step.data.arguments && (
                      <div className="mt-2">
                        <Typography variant="caption" className="font-semibold">
                          Arguments:
                        </Typography>
                        <pre className="text-xs mt-1 p-2 bg-gray-100 rounded overflow-x-auto">
                          {JSON.stringify(step.data.arguments, null, 2)}
                        </pre>
                      </div>
                    )}
                  </Paper>
                </div>
              )}

              {/* Tool Result */}
              {step.type === 'tool_result' && step.data && (
                <div>
                  <Typography variant="caption" className="font-semibold block mb-2">
                    📊 Result
                  </Typography>
                  <Paper variant="outlined" className="p-3">
                    {step.data.error ? (
                      <Typography variant="body2" color="error">
                        ❌ {step.data.error}
                      </Typography>
                    ) : (
                      <pre className="text-xs p-2 bg-gray-100 rounded overflow-x-auto max-h-64">
                        {typeof step.data.result === 'string'
                          ? step.data.result
                          : JSON.stringify(step.data.result, null, 2)}
                      </pre>
                    )}
                  </Paper>
                </div>
              )}

              {/* Metadata */}
              <div>
                <Typography variant="caption" className="font-semibold block mb-2">
                  📋 Metadata
                </Typography>
                <Paper variant="outlined" className="p-3">
                  <div className="space-y-1">
                    <Typography variant="body2">
                      <strong>ID:</strong> <code className="text-xs">{step.id}</code>
                    </Typography>
                    <Typography variant="body2">
                      <strong>Type:</strong> {step.type}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Status:</strong> {step.status}
                    </Typography>
                    {step.iteration && (
                      <Typography variant="body2">
                        <strong>Iteration:</strong> {step.iteration}
                      </Typography>
                    )}
                    {typeof step.duration === 'number' && (
                      <Typography variant="body2">
                        <strong>Duration:</strong> {Math.round(step.duration)}ms
                      </Typography>
                    )}
                  </div>
                </Paper>
              </div>
            </div>
          </TabPanel>

          <TabPanel value={activeTab} index={1}>
            {/* Data Tab */}
            <div className="space-y-2">
              <div className="flex justify-end">
                <Button
                  size="small"
                  startIcon={<Icon>content_copy</Icon>}
                  onClick={() => copyToClipboard(JSON.stringify(step.data, null, 2))}
                >
                  Copy JSON
                </Button>
              </div>
              <Paper variant="outlined" className="p-3">
                <pre className="text-xs overflow-x-auto">{JSON.stringify(step.data, null, 2)}</pre>
              </Paper>
            </div>
          </TabPanel>

          {step.data?.reasoning && (
            <TabPanel value={activeTab} index={2}>
              {/* Thinking Tab */}
              <Paper variant="outlined" className="p-4 bg-blue-50">
                <Typography variant="body1" className="whitespace-pre-wrap">
                  {step.data.reasoning}
                </Typography>
              </Paper>

              {/* Additional reflection details if available */}
              {step.data?.reflection_what_worked && (
                <Paper variant="outlined" className="p-3 mt-3 bg-green-50">
                  <Typography variant="caption" className="font-semibold block mb-2">
                    ✅ What Worked
                  </Typography>
                  <ul className="list-disc list-inside space-y-1">
                    {(step.data.reflection_what_worked as any[]).map((item, idx) => (
                      <li key={idx}>
                        <Typography variant="body2" component="span">
                          {String(item)}
                        </Typography>
                      </li>
                    ))}
                  </ul>
                </Paper>
              )}

              {step.data?.reflection_what_failed && (
                <Paper variant="outlined" className="p-3 mt-3 bg-red-50">
                  <Typography variant="caption" className="font-semibold block mb-2">
                    ❌ What Failed
                  </Typography>
                  <ul className="list-disc list-inside space-y-1">
                    {(step.data.reflection_what_failed as any[]).map((item, idx) => (
                      <li key={idx}>
                        <Typography variant="body2" component="span">
                          {String(item)}
                        </Typography>
                      </li>
                    ))}
                  </ul>
                </Paper>
              )}

              {typeof step.data?.reflection_confidence === 'number' && (
                <Paper variant="outlined" className="p-3 mt-3">
                  <Typography variant="caption" className="font-semibold block mb-2">
                    📊 Confidence
                  </Typography>
                  <Typography variant="body2">
                    {Math.round(step.data.reflection_confidence * 100)}%
                  </Typography>
                </Paper>
              )}
            </TabPanel>
          )}
        </Box>
      </Box>
    </Drawer>
  );
}
